<template>
  <div>
    <v-form ref="dtp-form" class="dtp-form" @submit.prevent="onSubmit">
      <v-overlay :value="loading">
        <v-progress-circular indeterminate large />
      </v-overlay>

      <div class="dtp-form__section">
        <auto-select
          :label="$t('auto.fields.number')"
          :dense="false"
          :rules="[isRequired]"
          @change="formData.car_id = $event.car_id"
        />
        <driver-select
          :label="$t('common.fields.phone')"
          :dense="false"
          :rules="[isRequired]"
          @change="formData.driver_id = $event.driver_id"
        />
        <v-select
          v-model="formData.status"
          item-text="name"
          item-value="id"
          :label="$t('common.fields.status')"
          :items="properties.car_statuses"
        />
        <menu-datepicker
          :label="$t('dtp.date')"
          :rules="[isRequired]"
          @change="formData.accident_date = $event"
        />
        <v-select
          v-model="formData.accident_causer"
          item-text="name"
          item-value="id"
          :label="$t('dtp.culprit')"
          :items="properties.accident_causers"
        />
        <v-text-field
          v-model="formData.damage_description"
          :label="$t('dtp.damageDescription')"
        />
        <type-select
          v-model="formData.accident_hide"
          :label="$t('dtp.isHidden')"
        />
        <v-text-field
          v-model="formData.period_restoration"
          :label="$t('dtp.period_restoration')"
        />
      </div>

      <div class="dtp-form__section">
        <h3 class="dtp-form__section-title">
          {{ $t('dtp.narcology') }}
        </h3>

        <type-select
          v-model="formData.drug_test"
          :label="$t('dtp.narcology')"
        />
        <v-text-field
          :label="$t('dtp.requestDate')"
          :disabled="!formData.drug_test"
          :value="formData.drug_report_datetime"
          @focus.prevent="
            () => {
              dateTimeDialog.opened = true;
              dateTimeDialog.param = 'drug_report_datetime';
            }
          "
        />
        <type-select
          v-model="formData.drug_conclusion"
          :label="$t('dtp.conclusion')"
          :disabled="!formData.drug_test"
        />
        <v-text-field
          :label="$t('dtp.conclusionDate')"
          :disabled="!formData.drug_test"
          :value="formData.drug_conclusion_datetime"
          @focus.prevent="
            () => {
              dateTimeDialog.opened = true;
              dateTimeDialog.param = 'drug_conclusion_datetime';
            }
          "
        />
      </div>
      <div class="dtp-form__section">
        <h3 class="dtp-form__section-title">
          {{ $t('dtp.insurance') }}
        </h3>

        <v-select
          v-model="formData.is_insurance"
          item-text="name"
          item-value="id"
          :items="properties.selects"
          :label="$t('dtp.insuranceRequest')"
        />
        <v-text-field
          v-model="formData.insurance_name"
          :label="$t('dtp.insuranceName')"
          :disabled="formData.is_insurance !== 1"
        />
        <menu-datepicker
          :label="$t('dtp.insuranceRequestDate')"
          :disabled="formData.is_insurance !== 1"
          @change="formData.insurance_application_date = $event"
        />
        <v-select
          v-model="formData.is_insurance_our"
          item-text="name"
          item-value="id"
          :disabled="formData.is_insurance !== 1"
          :label="$t('dtp.insuranceType')"
          :items="properties.insurances"
        />
      </div>
      <div class="dtp-form__section">
        <h3 class="dtp-form__section-title">
          {{ $t('dtp.materials') }}
        </h3>

        <type-select
          v-model="formData.referral_assessment_materials"
          :label="$t('dtp.materials')"
        />
        <menu-datepicker
          :label="$t('dtp.materialsDate')"
          :disabled="!formData.referral_assessment_materials"
          @change="formData.referral_assessment_materials_date = $event"
        />
      </div>
      <div class="dtp-form__section">
        <h3 class="dtp-form__section-title">
          {{ $t('dtp.assessmentReferral') }}
        </h3>

        <type-select
          v-model="formData.referral_assessment_assessment"
          :label="$t('dtp.assessmentReferral')"
        />
        <menu-datepicker
          :label="$t('dtp.assessmentReferralDate')"
          :disabled="!formData.referral_assessment_assessment"
          @change="formData.referral_assessment_assessment_date = $event"
        />
        <v-text-field
          v-model="formData.referral_assessment_company"
          :label="$t('dtp.assessmentName')"
          :disabled="!formData.referral_assessment_assessment"
        />
      </div>
      <div class="dtp-form__section">
        <h3 class="dtp-form__section-title">
          {{ $t('dtp.grade') }}
        </h3>

        <type-select v-model="formData.assessment" :label="$t('dtp.grade')" />
        <menu-datepicker
          :label="$t('dtp.passDate')"
          :disabled="!formData.assessment"
          @change="formData.assessment_date = $event"
        />
        <menu-datepicker
          :label="$t('dtp.reportDate')"
          :disabled="!formData.assessment"
          @change="formData.assessment_report_date = $event"
        />
        <v-text-field
          v-model="formData.assessment_amount"
          :label="$t('dtp.gradeAmount')"
          :disabled="!formData.assessment"
          :rules="[onlyNumbers]"
        />
      </div>
      <div class="dtp-form__section">
        <h3 class="dtp-form__section-title">
          {{ $t('dtp.autoRecovery') }}
        </h3>

        <type-select
          v-model="formData.auto_restoration"
          :label="$t('dtp.autoRecovery')"
        />
        <v-select
          v-model="formData.auto_restoration_type"
          item-text="name"
          item-value="id"
          :disabled="!formData.auto_restoration"
          :label="$t('dtp.troubleshootingType')"
          :items="properties.repair_types"
        />
        <menu-datepicker
          :label="$t('dtp.troubleshootingDate')"
          :disabled="!formData.auto_restoration"
          @change="formData.auto_troubleshooting_date = $event"
        />
        <v-text-field
          v-model="formData.auto_troubleshooting_amount"
          :label="$t('dtp.troubleshootingAmount')"
          :disabled="!formData.auto_restoration"
          :rules="[onlyNumbers]"
        />
        <type-select
          v-model="formData.owner_price_accept"
          :label="$t('dtp.coordinationWithOwner')"
          :disabled="!formData.auto_restoration"
        />
        <v-select
          v-model="formData.auto_restoration_status"
          item-text="name"
          item-value="id"
          :disabled="!formData.auto_restoration"
          :label="$t('dtp.readyStatus')"
          :items="properties.auto_restoration_statuses"
        />
        <menu-datepicker
          :disabled="!formData.auto_restoration"
          :label="$t('dtp.troubleshootingEndDate')"
          @change="formData.auto_troubleshooting_end_date = $event"
        />
      </div>
      <div class="dtp-form__section">
        <h3 class="dtp-form__section-title">
          {{ $t('dtp.pay') }}
        </h3>

        <type-select v-model="formData.payout" :label="$t('dtp.pay')" />
        <menu-datepicker
          :label="$t('dtp.courtStatement')"
          :disabled="!formData.payout"
          @change="formData.tribunal_act_date = $event"
        />
        <menu-datepicker
          :label="$t('dtp.paymentApplicationDate')"
          :disabled="!formData.payout"
          @change="formData.application_declaration_payout_date = $event"
        />
        <menu-datepicker
          :label="$t('dtp.paymentDate')"
          :disabled="!formData.payout"
          @change="formData.payout_date = $event"
        />
        <v-text-field
          v-model="formData.payout_amount"
          :label="$t('dtp.paymentAmount')"
          :disabled="!formData.payout"
          :rules="[onlyNumbers]"
        />
      </div>

      <v-btn
        type="submit"
        class="dtp-form__submit"
        color="warning"
        elevation="0"
      >
        {{ $t('common.btns.save') }}
      </v-btn>
    </v-form>

    <date-time-picker
      v-model="dateTimeDialog.opened"
      :date-time="formData[dateTimeDialog.param]"
      @change="formData[dateTimeDialog.param] = $event"
    />

    <v-snackbar v-model="snack.value" :color="snack.color" bottom right>
      {{ snack.message }}
    </v-snackbar>
  </div>
</template>

<script>
import AutoSelect from '@/components/shared/AutoSelect.vue';
import DriverSelect from '@/components/shared/DriverSelect.vue';
import MenuDatepicker from '@/components/shared/MenuDatepicker.vue';
import TypeSelect from './TypeSelect.vue';
import DateTimePicker from '../shared/DateTimePicker.vue';

export default {
  components: {
    AutoSelect,
    DriverSelect,
    MenuDatepicker,
    TypeSelect,
    DateTimePicker,
  },
  data: () => ({
    loading: false,
    dateTimeDialog: {
      opened: false,
      param: '',
    },
    snack: {
      value: false,
      message: '',
      color: '',
    },
    formData: {
      car_id: '',
      driver_id: '',
      status: '',
      accident_date: '',
      accident_causer: '',
      damage_description: '',
      period_restoration: '',
      accident_hide: '',
      drug_test: '',
      drug_report_datetime: '',
      drug_conclusion: '',
      drug_conclusion_datetime: '',
      is_insurance: '',
      insurance_name: '',
      insurance_application_date: '',
      is_insurance_our: '',
      referral_assessment_materials: '',
      referral_assessment_materials_date: '',
      referral_assessment_assessment: '',
      referral_assessment_assessment_date: '',
      referral_assessment_company: '',
      assessment: '',
      assessment_date: '',
      assessment_report_date: '',
      assessment_amount: '',
      auto_restoration: '',
      auto_troubleshooting_date: '',
      auto_restoration_type: '',
      auto_troubleshooting_amount: '',
      owner_price_accept: '',
      auto_restoration_status: '',
      auto_troubleshooting_end_date: '',
      payout: '',
      tribunal_act_date: '',
      application_declaration_payout_date: '',
      payout_date: '',
      payout_amount: '',
    },
  }),
  computed: {
    properties() {
      return this.$store.state.dtp.properties;
    },
  },
  methods: {
    isRequired(v) {
      return !!v || this.$t('common.fields.validation.field.required');
    },
    onlyNumbers(v) {
      if (!v) return true;
      return (
        /^[0-9]{1,12}$/.test(v) || this.$t('common.fields.validation.numbers')
      );
    },
    async onSubmit() {
      const isValid = this.$refs['dtp-form'].validate();

      if (!isValid) {
        window.scrollTo(0, 0);
        return;
      }

      this.loading = true;
      const { success } = await this.$store.dispatch(
        'dtp/addNew',
        this.formData,
      );

      const snack = {
        value: true,
        message: this.$t('common.saved'),
        color: 'success',
      };

      if (!success) {
        snack.message = this.$t('common.errors.APIerror');
        snack.color = 'error';
      } else {
        setTimeout(() => {
          this.$router.push('/dtp');
        }, 1500);
      }

      this.snack = snack;
      this.loading = false;
    },
  },
  async created() {
    const propertiesLoaded = Object.keys(this.properties).length;

    if (propertiesLoaded) return;

    this.loading = true;
    await this.$store.dispatch('dtp/fetchProperties');
    this.loading = false;
  },
};
</script>

<style lang="scss">
.dtp-form {
  margin-top: 30px;
  padding-bottom: 20px;

  &__section {
    display: flex;
    flex-wrap: wrap;

    &-title {
      max-width: 100% !important;
      margin-top: 15px;
      margin-bottom: 15px;
      color: #464646;
    }

    > * {
      margin-right: 30px;
    }

    > *,
    .v-input {
      width: 100%;
      max-width: 300px;
    }
  }

  &__submit {
    display: block;
    max-width: 250px;
    width: 100%;
    margin-top: 30px;
  }

  .v-text-field__slot input,
  .v-select__slot input {
    margin-top: 2px;
    margin-bottom: 2px;
  }
}
</style>
